import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Shield, Scale, Coins, Globe, UserX, FileText } from 'lucide-react';

const TermsModal = ({ open, onClose }) => {
  if (!open) return null;

  const sections = [
    {
      icon: <AlertTriangle className="w-5 h-5 text-amber-400" />,
      title: "1. Acceptance of Terms",
      content: `By accessing or using the Shut The Box website, application, or any associated services (collectively, the "Platform"), you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions. If you do not agree to these terms, you must not use the Platform.

These terms constitute a legally binding agreement between you and Shut The Box. We reserve the right to modify these terms at any time without prior notice. Your continued use of the Platform following any changes constitutes acceptance of those changes.`
    },
    {
      icon: <Shield className="w-5 h-5 text-blue-400" />,
      title: "2. No Gambling — Skill-Based Entertainment",
      content: `Shut The Box is a skill-based strategy game. The game mechanics reward strategic decision-making, not chance. While dice are used as a randomization element (similar to many board games), success in Shut The Box depends primarily on the player's ability to make optimal tile selections based on probability and game state analysis.

This Platform does NOT constitute gambling, betting, or wagering as defined by most jurisdictions. No real money is wagered on outcomes determined purely by chance. Any token-based features are designed as rewards for skilled gameplay and platform participation, not gambling winnings.

You acknowledge and agree that:
• Shut The Box is entertainment software, not a gambling service
• Outcomes depend substantially on player skill and strategy
• The Platform does not guarantee any financial returns
• Participation is voluntary and for entertainment purposes only`
    },
    {
      icon: <Coins className="w-5 h-5 text-yellow-400" />,
      title: "3. Cryptocurrency & Token Disclaimer",
      content: `The $STBOX token and any other digital assets associated with this Platform are utility tokens intended for use within the Shut The Box ecosystem. They are NOT securities, investment contracts, or financial instruments.

IMPORTANT DISCLAIMERS:
• Cryptocurrency values are highly volatile and can lose all value
• Past performance does not indicate future results
• You may lose some or all of your investment
• We make no guarantees regarding token value, liquidity, or future utility
• Tokens should not be purchased with funds you cannot afford to lose
• This is not financial advice — consult a qualified financial advisor

By acquiring or using $STBOX tokens, you represent that you:
• Understand the risks associated with cryptocurrency
• Are not relying on any promises of profit or returns
• Accept full responsibility for your own financial decisions
• Have conducted your own research and due diligence`
    },
    {
      icon: <Scale className="w-5 h-5 text-purple-400" />,
      title: "4. Limitation of Liability",
      content: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:

The Platform, its creators, developers, team members, and affiliates shall NOT be liable for any direct, indirect, incidental, special, consequential, or punitive damages, including but not limited to:
• Loss of profits, tokens, or cryptocurrency
• Loss of data or business interruption
• Personal injury or property damage
• Any damages arising from your use or inability to use the Platform
• Any unauthorized access to or alteration of your data
• Any bugs, viruses, or errors in the Platform
• Any third-party conduct or content

THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

You agree to use the Platform at your own risk and acknowledge that we are not responsible for any losses you may incur.`
    },
    {
      icon: <Globe className="w-5 h-5 text-green-400" />,
      title: "5. Jurisdictional Restrictions",
      content: `You are solely responsible for determining whether your use of the Platform complies with applicable laws in your jurisdiction. The Platform is not intended for use in any jurisdiction where such use would be prohibited or restricted.

BY USING THIS PLATFORM, YOU REPRESENT AND WARRANT THAT:
• You are at least 18 years of age (or the age of majority in your jurisdiction)
• Your use of the Platform does not violate any applicable law or regulation
• You are not located in, or a citizen or resident of, any jurisdiction where cryptocurrency or blockchain-based gaming is prohibited
• You are not on any sanctions list or prohibited from engaging in cryptocurrency transactions
• You will not use the Platform for any illegal or unauthorized purpose

We reserve the right to restrict access to users from any jurisdiction at our sole discretion, without prior notice.`
    },
    {
      icon: <UserX className="w-5 h-5 text-red-400" />,
      title: "6. Prohibited Conduct",
      content: `You agree NOT to:
• Use the Platform for any illegal purpose or in violation of any laws
• Attempt to manipulate, exploit, or abuse the Platform or its systems
• Use bots, scripts, or automated systems to interact with the Platform
• Engage in fraud, money laundering, or other financial crimes
• Harass, threaten, or harm other users
• Impersonate any person or entity
• Interfere with the proper functioning of the Platform
• Attempt to reverse engineer, decompile, or hack the Platform
• Circumvent any security measures or access restrictions
• Create multiple accounts to abuse rewards or promotions

Violation of these terms may result in immediate termination of your access to the Platform, forfeiture of any tokens or rewards, and potential legal action.`
    },
    {
      icon: <FileText className="w-5 h-5 text-pink-400" />,
      title: "7. Intellectual Property & Miscellaneous",
      content: `INTELLECTUAL PROPERTY:
All content, trademarks, logos, and intellectual property on the Platform are owned by or licensed to Shut The Box. You may not copy, reproduce, distribute, or create derivative works without our express written permission.

INDEMNIFICATION:
You agree to indemnify, defend, and hold harmless the Platform and its team from any claims, damages, losses, or expenses arising from your use of the Platform or violation of these terms.

DISPUTE RESOLUTION:
Any disputes shall be resolved through binding arbitration in accordance with applicable arbitration rules. You waive any right to participate in class action lawsuits.

SEVERABILITY:
If any provision of these terms is found invalid or unenforceable, the remaining provisions shall continue in full force and effect.

ENTIRE AGREEMENT:
These Terms & Conditions, along with any other policies referenced herein, constitute the entire agreement between you and Shut The Box.

CONTACT:
For questions about these terms, please reach out through our official Discord or X (Twitter) channels.

Last Updated: December 2025`
    }
  ];

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100]">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="absolute inset-4 md:inset-8 lg:inset-16 flex items-center justify-center"
          >
            <div className="relative w-full max-w-4xl max-h-full bg-gradient-to-b from-[#2e032e] to-[#1a011a] rounded-2xl shadow-2xl border border-white/10 overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-white/10 bg-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-pink-500/20 rounded-lg">
                      <Scale className="w-6 h-6 text-pink-400" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-white">Terms & Conditions</h2>
                      <p className="text-sm text-white/60">Please read carefully before using Shut The Box</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Warning Banner */}
              <div className="flex-shrink-0 mx-6 mt-4 p-4 bg-amber-500/10 border border-amber-400/30 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-100">
                    <strong className="font-semibold">Important:</strong> By using Shut The Box, you acknowledge that you have read, understood, and agree to be bound by these terms. Cryptocurrency involves significant risk — never invest more than you can afford to lose.
                  </div>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                {sections.map((section, index) => (
                  <div
                    key={index}
                    className="bg-white/5 rounded-xl border border-white/10 overflow-hidden"
                  >
                    <div className="flex items-center gap-3 px-4 py-3 bg-white/5 border-b border-white/10">
                      {section.icon}
                      <h3 className="font-semibold text-white">{section.title}</h3>
                    </div>
                    <div className="px-4 py-4">
                      <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">
                        {section.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t border-white/10 bg-white/5">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <p className="text-xs text-white/50 text-center sm:text-left">
                    By continuing to use Shut The Box, you agree to these terms.
                  </p>
                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold rounded-full transition-all duration-300 shadow-lg hover:shadow-pink-500/25"
                  >
                    I Understand & Accept
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TermsModal;
